import { Button } from '@/shared/components/ui/Button';
import { useEscapeClose } from '@/shared/hooks/useEscapeClose';
import { ApiError } from '@/shared/lib/api-client';
import { confirm } from '@/shared/lib/confirm';
import { useAuthStore } from '@/shared/stores/auth-store';
import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ChevronDown,
  ChevronUp,
  ChevronsUpDown,
  Combine,
  Download,
  Edit2,
  Filter,
  Loader2,
  MessageSquare,
  Search,
  Trash2,
  Upload,
  User,
  UserPlus,
  Users,
} from 'lucide-react';

import { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import {
  bulkDeleteContacts,
  type ContactRow,
  deleteAllContacts,
  downloadContactsExport,
  listContacts,
} from './api';
import { ContactFormModal } from './ContactFormModal';
import { ImportModal } from './ImportModal';
import { MergeModal } from './MergeModal';
import { useRefetchOnOpen } from '@/shared/hooks/useRefetchOnOpen';
import { useAccessGroups, useContactTags } from './hooks';

const PAGE_SIZE = 50;

export function ContatosPage() {
  const qc = useQueryClient();
  // Apagar (um, vários, todos), importar, exportar e mesclar seguem só do admin
  // (perfil de acesso com a tela Contatos vê/cria/edita — o servidor recusa o
  // resto, então os botões nem aparecem).
  const isAdmin = useAuthStore((s) => s.user?.profile) === 'admin';
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selectedTagIds, setSelectedTagIds] = useState<number[]>([]);
  const [tagFilterOpen, setTagFilterOpen] = useState(false);
  const tagFilterRef = useRef<HTMLDivElement>(null);
  const [selectedAccessGroupIds, setSelectedAccessGroupIds] = useState<number[]>([]);
  const [groupFilterOpen, setGroupFilterOpen] = useState(false);
  const groupFilterRef = useRef<HTMLDivElement>(null);

  // Fecha o dropdown de etiquetas ao clicar fora (mesmo padrão da Consulta).
  useEffect(() => {
    if (!tagFilterOpen) return;
    const handler = (e: MouseEvent): void => {
      if (tagFilterRef.current && !tagFilterRef.current.contains(e.target as Node)) {
        setTagFilterOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [tagFilterOpen]);

  // Fecha o dropdown de grupos de acesso ao clicar fora.
  useEffect(() => {
    if (!groupFilterOpen) return;
    const handler = (e: MouseEvent): void => {
      if (groupFilterRef.current && !groupFilterRef.current.contains(e.target as Node)) {
        setGroupFilterOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [groupFilterOpen]);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [editing, setEditing] = useState<ContactRow | null>(null);
  const [merging, setMerging] = useState<ContactRow | null>(null);
  const [creating, setCreating] = useState(false);
  const [importing, setImporting] = useState(false);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [confirmDeleteAllOpen, setConfirmDeleteAllOpen] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  const tagsQ = useContactTags();
  useRefetchOnOpen(tagFilterOpen, tagsQ.refetch);
  const groupsQ = useAccessGroups();
  const groupsEnabled = (groupsQ.data ?? []).length > 0;

  const listQ = useInfiniteQuery({
    queryKey: [
      'contacts',
      { q: debouncedSearch, tagIds: selectedTagIds, accessGroupIds: selectedAccessGroupIds },
    ],
    queryFn: ({ pageParam }) =>
      listContacts({
        q: debouncedSearch || undefined,
        tagIds: selectedTagIds.length > 0 ? selectedTagIds : undefined,
        accessGroupIds: selectedAccessGroupIds.length > 0 ? selectedAccessGroupIds : undefined,
        limit: PAGE_SIZE,
        ...(typeof pageParam === 'string' && { cursor: pageParam }),
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.next_cursor ?? undefined,
    placeholderData: (prev) => prev,
    // Descarta o cache ao sair da tela → volta sempre recomeçando no 50 (1ª
    // página), em vez de restaurar todas as páginas roladas na visita anterior.
    gcTime: 0,
  });

  const deleteMut = useMutation({
    mutationFn: () =>
      bulkDeleteContacts({ contact_ids: Array.from(selected), confirmation: 'APAGAR' }),
    onSuccess: () => {
      setSelected(new Set());
      setConfirmDeleteOpen(false);
      void qc.invalidateQueries({ queryKey: ['contacts'] });
    },
  });

  const deleteAllMut = useMutation({
    mutationFn: () => deleteAllContacts({ confirmation: 'APAGAR' }),
    onSuccess: (res) => {
      setConfirmDeleteAllOpen(false);
      setSelected(new Set());
      void qc.invalidateQueries({ queryKey: ['contacts'] });
      const kept =
        res.soft_deleted > 0
          ? ` (${res.soft_deleted.toLocaleString('pt-BR')} com atendimento foram ocultados — histórico preservado)`
          : '';
      toast.success(`${res.deleted.toLocaleString('pt-BR')} contatos apagados${kept}.`);
    },
  });

  // Flatten todas as páginas em ordem (page[0] = mais recentes, página seguinte = mais antigos).
  const contacts = useMemo<ContactRow[]>(
    () => listQ.data?.pages.flatMap((p) => p.contacts) ?? [],
    [listQ.data],
  );
  const total = listQ.data?.pages[0]?.total ?? 0;

  // Ordenação client-side (sobre os contatos já carregados) — mesmo padrão da
  // Consulta. Clica no chevron pra alternar asc → desc → sem ordem.
  const [sortBy, setSortBy] = useState<ContactSortKey | null>(null);
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const toggleSort = (key: ContactSortKey): void => {
    if (sortBy !== key) {
      setSortBy(key);
      setSortDir('asc');
      return;
    }
    if (sortDir === 'asc') {
      setSortDir('desc');
    } else {
      setSortBy(null);
    }
  };
  const sortedContacts = useMemo(
    () => (sortBy ? sortContacts(contacts, sortBy, sortDir) : contacts),
    [contacts, sortBy, sortDir],
  );

  // IntersectionObserver no final da lista — auto-load próxima página quando
  // o sentinel entra no viewport. Threshold 0 = dispara assim que aparecer
  // 1px do sentinel. rootMargin '200px' = pré-carrega antes do user chegar
  // ao fim, suavizando a UX.
  const sentinelRef = useRef<HTMLTableRowElement | null>(null);
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || !listQ.hasNextPage || listQ.isFetchingNextPage) return;
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            void listQ.fetchNextPage();
          }
        }
      },
      { rootMargin: '200px', threshold: 0 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [listQ.hasNextPage, listQ.isFetchingNextPage, listQ.fetchNextPage]);
  const allChecked = contacts.length > 0 && contacts.every((c) => selected.has(c.id));
  // Nº de colunas da tabela (pra colSpan das linhas de estado). Base = 7
  // (check, nome, whatsapp, etiquetas, 1º contato, últ. contato, ações); +1 se
  // a coluna de grupos estiver visível.
  const colCount = groupsEnabled ? 8 : 7;

  const toggleAll = (): void => {
    if (allChecked) {
      const next = new Set(selected);
      for (const c of contacts) next.delete(c.id);
      setSelected(next);
    } else {
      const next = new Set(selected);
      for (const c of contacts) next.add(c.id);
      setSelected(next);
    }
  };

  const toggleOne = (id: number): void => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleDeleteOne = (id: number): void => {
    setSelected(new Set([id]));
    setConfirmDeleteOpen(true);
  };

  return (
    <div className="flex h-full flex-1 flex-col overflow-hidden bg-n-50">
      {/* Page header */}
      <header className="flex flex-shrink-0 items-center justify-between border-b border-n-200 bg-n-0 px-5 py-4">
        <div>
          <h1
            style={{ fontSize: '22px', fontWeight: 700, letterSpacing: '-.02em', color: 'var(--n-900)' }}
          >
            Contatos
          </h1>
          <p className="text-[12px] text-n-500">
            {contacts.length === total
              ? `${total.toLocaleString('pt-BR')} contatos`
              : `Mostrando ${contacts.length.toLocaleString('pt-BR')} de ${total.toLocaleString('pt-BR')} contatos`}
          </p>
        </div>
        <div className="flex gap-2">
          {isAdmin && (
            <>
              <Button
                variant="danger"
                onClick={() => setConfirmDeleteAllOpen(true)}
                disabled={
                  total === 0 ||
                  debouncedSearch !== '' ||
                  selectedTagIds.length > 0 ||
                  selectedAccessGroupIds.length > 0
                }
                title={
                  debouncedSearch !== '' ||
                  selectedTagIds.length > 0 ||
                  selectedAccessGroupIds.length > 0
                    ? 'Limpe a busca e os filtros — "Apagar todos" age sobre a base inteira'
                    : undefined
                }
              >
                <Trash2 aria-hidden="true" />
                Apagar todos
              </Button>
              <Button variant="outline" onClick={() => setImporting(true)}>
                <Upload aria-hidden="true" />
                Importar
              </Button>
              <Button
                variant="outline"
                onClick={async () => {
                  const ok = await confirm({
                    title: 'Exportar contatos',
                    description:
                      'Essa ação irá exportar todos os contatos cadastrados em formato CSV. A exportação pode levar alguns minutos. Tem certeza que deseja prosseguir?',
                    confirmText: 'Exportar contatos',
                    cancelText: 'Cancelar',
                  });
                  if (!ok) return;
                  toast.info('Gerando CSV…');
                  void downloadContactsExport('csv').catch(() => toast.error('Falha ao exportar'));
                }}
              >
                <Download aria-hidden="true" />
                Exportar CSV
              </Button>
            </>
          )}
          <Button onClick={() => setCreating(true)}>
            <UserPlus aria-hidden="true" />
            Adicionar contato
          </Button>
        </div>
      </header>

      {/* Toolbar */}
      <div
        className="flex flex-shrink-0 items-center border-b border-n-200 bg-n-0 px-5 py-3"
        style={{ gap: '10px' }}
      >
        {/* Search — .input-search style */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            background: 'var(--bg-surface)',
            border: '1px solid var(--n-300)',
            borderRadius: 'var(--r-md)',
            height: '36px',
            padding: '0 12px',
            flex: 1,
          }}
        >
          <Search size={14} strokeWidth={1.75} style={{ color: 'var(--n-400)', flexShrink: 0 }} />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar por nome, telefone ou e-mail…"
            style={{
              flex: 1,
              border: 'none',
              outline: 'none',
              background: 'transparent',
              fontFamily: 'inherit',
              fontSize: '13px',
              color: 'var(--n-900)',
            }}
          />
        </div>

        {/* Tag filter */}
        <div ref={tagFilterRef} className="relative">
          <Button variant="outline" onClick={() => setTagFilterOpen((o) => !o)}>
            <Filter aria-hidden="true" />
            Filtrar por etiqueta {selectedTagIds.length > 0 && `(${selectedTagIds.length})`}
          </Button>
          {tagFilterOpen && (
            <div className="absolute right-0 left-0 top-full z-40 mt-2 rounded-md border border-n-200 bg-n-0 p-2 shadow-md">
              {(tagsQ.data ?? []).length === 0 && (
                <div className="px-3 py-2 text-[12px] text-n-500">Nenhuma etiqueta cadastrada</div>
              )}
              {(tagsQ.data ?? []).map((t) => {
                const active = selectedTagIds.includes(t.id);
                return (
                  <label
                    key={t.id}
                    className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-[13px] text-n-800 hover:bg-n-50"
                  >
                    <input
                      type="checkbox"
                      checked={active}
                      onChange={() =>
                        setSelectedTagIds((prev) =>
                          active ? prev.filter((id) => id !== t.id) : [...prev, t.id],
                        )
                      }
                    />
                    <span
                      className="h-3 w-3 flex-shrink-0 rounded-pill"
                      style={{ background: t.color }}
                    />
                    <span className="truncate">{t.name}</span>
                  </label>
                );
              })}
            </div>
          )}
        </div>

        {/* Access group filter — só aparece se o tenant tem grupos cadastrados. */}
        {groupsEnabled && (
          <div ref={groupFilterRef} className="relative">
            <Button variant="outline" onClick={() => setGroupFilterOpen((o) => !o)}>
              <Filter aria-hidden="true" />
              Filtrar por grupo{' '}
              {selectedAccessGroupIds.length > 0 && `(${selectedAccessGroupIds.length})`}
            </Button>
            {groupFilterOpen && (
              <div className="absolute right-0 left-0 top-full z-40 mt-2 rounded-md border border-n-200 bg-n-0 p-2 shadow-md">
                {(groupsQ.data ?? []).length === 0 && (
                  <div className="px-3 py-2 text-[12px] text-n-500">Nenhum grupo cadastrado</div>
                )}
                {(groupsQ.data ?? []).map((g) => {
                  const active = selectedAccessGroupIds.includes(g.id);
                  return (
                    <label
                      key={g.id}
                      className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-[13px] text-n-800 hover:bg-n-50"
                    >
                      <input
                        type="checkbox"
                        checked={active}
                        onChange={() =>
                          setSelectedAccessGroupIds((prev) =>
                            active ? prev.filter((id) => id !== g.id) : [...prev, g.id],
                          )
                        }
                      />
                      <span className="truncate">{g.name}</span>
                    </label>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Bulk selection bar */}
      {selected.size > 0 && isAdmin && (
        <div className="flex flex-shrink-0 items-center justify-between border-b border-warning-200 bg-warning-50 px-5 py-2">
          <span className="font-semibold text-[12px] text-warning-700">
            {selected.size} contato(s) selecionado(s)
          </span>
          <div className="flex gap-2">
            <Button
              variant="ghost"
              onClick={() => setSelected(new Set())}
              className="text-[12px]"
            >
              Cancelar
            </Button>
            <Button
              variant="primary"
              onClick={() => setConfirmDeleteOpen(true)}
              className="bg-danger-500 hover:bg-danger-600"
            >
              <Trash2 aria-hidden="true" />
              Apagar selecionados
            </Button>
          </div>
        </div>
      )}

      {/* Table area — scroll fica DENTRO do card, não na página inteira.
          Outer wrapper: flex column + overflow-hidden + flex-1 (provê espaço).
          Card SEM flex-1: sizing pelo conteúdo (scroll natural + footer). Se
          conteúdo excede o espaço disponível, card encolhe e scroll interno
          ativa. Lista curta → card pequeno, sem espaço vazio. */}
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden p-5">
        <div className="flex min-h-0 flex-col overflow-hidden rounded-md border border-n-200 bg-n-0 shadow-sm">
          <div className="min-h-0 flex-1 overflow-auto">
            <table className="w-full min-w-[700px] text-[12px]">
              <thead className="sticky top-0 z-10 border-b border-n-200 bg-n-50 text-left">
                <tr>
                  <th style={{ width: '34px' }} className="px-[14px] py-[10px]">
                    {isAdmin && (
                      <input
                        type="checkbox"
                        checked={allChecked}
                        onChange={toggleAll}
                        aria-label="Selecionar todos"
                      />
                    )}
                  </th>
                  <ContactSortTh
                    label="Nome"
                    sortKey="name"
                    currentKey={sortBy}
                    dir={sortDir}
                    onToggle={toggleSort}
                  />
                  <th className="px-[14px] py-[10px] text-[11px] font-bold uppercase tracking-[.05em] text-n-500 whitespace-nowrap">
                    WhatsApp
                  </th>
                  <ContactSortTh
                    label="Etiquetas"
                    sortKey="tags"
                    currentKey={sortBy}
                    dir={sortDir}
                    onToggle={toggleSort}
                  />
                  {groupsEnabled && (
                    <ContactSortTh
                      label="Grupos de acesso"
                      sortKey="group"
                      currentKey={sortBy}
                      dir={sortDir}
                      onToggle={toggleSort}
                    />
                  )}
                  <ContactSortTh
                    label="Primeiro Contato"
                    sortKey="first"
                    currentKey={sortBy}
                    dir={sortDir}
                    onToggle={toggleSort}
                  />
                  <ContactSortTh
                    label="Último Contato"
                    sortKey="last"
                    currentKey={sortBy}
                    dir={sortDir}
                    onToggle={toggleSort}
                  />
                  <th className="px-[14px] py-[10px] text-right text-[11px] font-bold uppercase tracking-[.05em] text-n-500 whitespace-nowrap">
                    Ações
                  </th>
                </tr>
              </thead>
              <tbody>
                {listQ.isLoading && (
                  <tr>
                    <td colSpan={colCount} className="py-12 text-center text-n-500">
                      <Loader2 size={20} className="mx-auto animate-spin text-n-400" />
                    </td>
                  </tr>
                )}
                {!listQ.isLoading && contacts.length === 0 && (
                  <tr>
                    <td colSpan={colCount} className="py-12 text-center text-n-500">
                      <Users size={32} className="mx-auto mb-2 text-n-300" />
                      Nenhum contato {debouncedSearch && `pra "${debouncedSearch}"`}
                    </td>
                  </tr>
                )}
                {sortedContacts.map((c) => (
                  <ContactRowItem
                    key={c.id}
                    contact={c}
                    showGroups={groupsEnabled}
                    selected={selected.has(c.id)}
                    podeAdministrar={isAdmin}
                    onToggle={() => toggleOne(c.id)}
                    onEdit={() => setEditing(c)}
                    onMerge={() => setMerging(c)}
                    onDelete={() => handleDeleteOne(c.id)}
                  />
                ))}
                {/* Sentinela: IntersectionObserver dispara fetchNextPage quando entra no viewport. */}
                {listQ.hasNextPage && (
                  <tr ref={sentinelRef}>
                    <td colSpan={colCount} className="py-4 text-center text-[12px] text-n-500">
                      {listQ.isFetchingNextPage ? (
                        <span className="inline-flex items-center gap-2">
                          <Loader2 size={14} className="animate-spin" /> Carregando mais…
                        </span>
                      ) : (
                        'Role para carregar mais'
                      )}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Footer — contagem à esquerda, "Limpar filtros" à direita (aparece com
              filtro ativo, mesmo com 0 resultados, pra sempre dar pra limpar). */}
          {(() => {
            const filtersActive =
              search.trim().length > 0 ||
              selectedTagIds.length > 0 ||
              selectedAccessGroupIds.length > 0;
            if (listQ.isLoading || (contacts.length === 0 && !filtersActive)) return null;
            return (
              <div
                style={{
                  padding: '10px 16px',
                  borderTop: '1px solid var(--n-100)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  fontSize: '12px',
                  color: 'var(--n-500)',
                }}
              >
                <span>
                  {contacts.length === total
                    ? `${total.toLocaleString('pt-BR')} contatos`
                    : `Mostrando ${contacts.length.toLocaleString('pt-BR')} de ${total.toLocaleString('pt-BR')}`}
                </span>
                {filtersActive && (
                  <button
                    type="button"
                    onClick={() => {
                      setSearch('');
                      setSelectedTagIds([]);
                      setSelectedAccessGroupIds([]);
                      setTagFilterOpen(false);
                      setGroupFilterOpen(false);
                    }}
                    className="font-semibold text-n-500 underline hover:text-n-700"
                  >
                    Limpar filtros
                  </button>
                )}
              </div>
            );
          })()}
        </div>
      </div>

      {(creating || editing) && (
        <ContactFormModal
          contact={editing ?? null}
          onClose={() => {
            setCreating(false);
            setEditing(null);
          }}
        />
      )}
      {importing && <ImportModal onClose={() => setImporting(false)} />}
      {merging && <MergeModal source={merging} onClose={() => setMerging(null)} />}
      {confirmDeleteOpen && (
        <ConfirmBulkDeleteModal
          count={selected.size}
          onCancel={() => {
            setConfirmDeleteOpen(false);
            setSelected(new Set());
          }}
          onConfirm={() => deleteMut.mutate()}
          loading={deleteMut.isPending}
          error={
            deleteMut.error instanceof ApiError
              ? deleteMut.error.problem.title
              : deleteMut.isError
                ? 'Falha ao apagar.'
                : null
          }
        />
      )}
      {confirmDeleteAllOpen && (
        <ConfirmBulkDeleteModal
          count={total}
          onCancel={() => setConfirmDeleteAllOpen(false)}
          onConfirm={() => deleteAllMut.mutate()}
          loading={deleteAllMut.isPending}
          error={
            deleteAllMut.error instanceof ApiError
              ? deleteAllMut.error.problem.title
              : deleteAllMut.isError
                ? 'Falha ao apagar.'
                : null
          }
        />
      )}
    </div>
  );
}

function ContactRowItem({
  contact,
  showGroups,
  selected,
  podeAdministrar,
  onToggle,
  onEdit,
  onMerge,
  onDelete,
}: {
  contact: ContactRow;
  showGroups: boolean;
  selected: boolean;
  /** Admin: seleção, mesclar e apagar (o resto do perfil só vê/edita). */
  podeAdministrar: boolean;
  onToggle: () => void;
  onEdit: () => void;
  onMerge: () => void;
  onDelete: () => void;
}) {
  const phone = contact.primary_identifier?.value ?? '—';
  const phoneFmt = useMemo(() => formatPhone(phone), [phone]);

  return (
    <tr className="border-b border-n-100 last:border-b-0 hover:bg-n-50">
      <td className="px-[14px] py-[11px]">
        {podeAdministrar && (
          <input type="checkbox" checked={selected} onChange={onToggle} aria-label="Selecionar" />
        )}
      </td>
      <td className="px-[14px] py-[11px]">
        <div className="flex items-center" style={{ gap: '8px' }}>
          <div className="relative flex h-[30px] w-[30px] flex-shrink-0 items-center justify-center overflow-hidden rounded-pill bg-n-100 text-n-400">
            {contact.profile_pic_url ? (
              <img
                src={contact.profile_pic_url}
                alt=""
                className="h-full w-full object-cover"
                onError={(e) => {
                  // Falha → esconde img, ícone fica visível atrás.
                  e.currentTarget.style.display = 'none';
                }}
              />
            ) : (
              <User size={16} strokeWidth={1.5} />
            )}
          </div>
          <span className="font-semibold text-n-900">{contact.display_name ?? 'Sem nome'}</span>
        </div>
      </td>
      <td className="px-[14px] py-[11px] text-[12px] text-n-600 whitespace-nowrap">{phoneFmt}</td>
      <td className="px-[14px] py-[11px]">
        <div className="flex flex-wrap gap-1">
          {contact.tags.map((t) => (
            <span
              key={t.id}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                padding: '3px 9px',
                borderRadius: 'var(--r-sm)',
                fontSize: '11px',
                fontWeight: 600,
                background: `${t.color}1f`,
                color: t.color,
              }}
            >
              {t.name}
            </span>
          ))}
        </div>
      </td>
      {showGroups && (
        <td className="px-[14px] py-[11px]">
          <div className="flex flex-wrap gap-1">
            {contact.access_groups.map((g) => (
              <span
                key={g.id}
                className="inline-flex items-center rounded-[var(--r-sm)] bg-n-100 px-[9px] py-[3px] font-semibold text-[11px] text-n-700"
              >
                {g.name}
              </span>
            ))}
          </div>
        </td>
      )}
      <td className="px-[14px] py-[11px] text-n-600 whitespace-nowrap">
        {new Date(contact.created_at).toLocaleDateString('pt-BR')}
      </td>
      <td className="px-[14px] py-[11px] text-n-600 whitespace-nowrap">
        {contact.last_inbound_at
          ? new Date(contact.last_inbound_at).toLocaleDateString('pt-BR')
          : '—'}
      </td>
      <td className="px-[14px] py-[11px]">
        <div className="flex justify-end" style={{ gap: '4px' }}>
          <button
            type="button"
            className="hover:bg-n-100 hover:text-brand-600"
            title="Iniciar atendimento (em breve)"
            disabled
            style={{
              width: '30px',
              height: '30px',
              borderRadius: 'var(--r-sm)',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'var(--n-500)',
              border: 'none',
              background: 'transparent',
              cursor: 'not-allowed',
              opacity: 0.5,
            }}
          >
            <MessageSquare size={14} strokeWidth={1.75} />
          </button>
          <button
            type="button"
            onClick={onEdit}
            title="Editar"
            className="hover:bg-n-100 hover:text-n-800"
            style={{
              width: '30px',
              height: '30px',
              borderRadius: 'var(--r-sm)',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'var(--n-500)',
              border: 'none',
              background: 'transparent',
              cursor: 'pointer',
            }}
          >
            <Edit2 size={14} strokeWidth={1.75} />
          </button>
          {podeAdministrar && (
            <>
              <button
                type="button"
                onClick={onMerge}
                title="Mesclar com outro"
                className="hover:bg-n-100 hover:text-n-800"
                style={{
                  width: '30px',
                  height: '30px',
                  borderRadius: 'var(--r-sm)',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'var(--n-500)',
                  border: 'none',
                  background: 'transparent',
                  cursor: 'pointer',
                }}
              >
                <Combine size={14} strokeWidth={1.75} />
              </button>
              <button
                type="button"
                onClick={onDelete}
                title="Apagar"
                className="hover:bg-danger-50 hover:text-danger-600"
                style={{
                  width: '30px',
                  height: '30px',
                  borderRadius: 'var(--r-sm)',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'var(--n-500)',
                  border: 'none',
                  background: 'transparent',
                  cursor: 'pointer',
                }}
              >
                <Trash2 size={14} strokeWidth={1.75} />
              </button>
            </>
          )}
        </div>
      </td>
    </tr>
  );
}

function ConfirmBulkDeleteModal({
  count,
  onCancel,
  onConfirm,
  loading,
  error,
}: {
  count: number;
  onCancel: () => void;
  onConfirm: () => void;
  loading: boolean;
  error: string | null;
}) {
  useEscapeClose(true, onCancel);
  // Bulk-delete (>1 contato) exige digitar "APAGAR" — friction contra
  // ação destrutiva massiva. Single delete (1 contato, da lixeira inline)
  // pula a confirmação textual.
  const isBulk = count > 1;
  const [text, setText] = useState('');
  const enabled = !isBulk || text === 'APAGAR';

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
      onClick={(e) => {
        if (e.target === e.currentTarget) onCancel();
      }}
    >
      <div className="w-[440px] rounded-lg bg-n-0 p-6 shadow-lg">
        <h2 className="font-bold text-[16px] text-n-900">
          {isBulk ? 'Apagar contatos' : 'Apagar contato'}
        </h2>
        <p className="mt-3 text-[13px] text-n-700">
          {isBulk ? (
            <>
              Você está prestes a apagar <strong>{count.toLocaleString('pt-BR')} contatos</strong>{' '}
              da lista. Os atendimentos dos contatos não serão excluídos — confirme digitando{' '}
              <strong>APAGAR</strong> abaixo.
            </>
          ) : (
            <>Confirma apagar este contato da lista?</>
          )}
        </p>
        {isBulk && (
          <input
            value={text}
            onChange={(e) => setText(e.target.value)}
            autoFocus
            className="mt-3 w-full rounded-md border border-n-200 bg-n-0 px-3 py-2 text-[13px] outline-none focus:border-danger-500"
            placeholder="APAGAR"
          />
        )}
        {error && (
          <div className="mt-3 rounded-md bg-danger-50 px-3 py-2 text-[12px] text-danger-700">
            {error}
          </div>
        )}
        <div className="mt-5 flex justify-end gap-2">
          <Button variant="ghost" onClick={onCancel} disabled={loading}>
            Cancelar
          </Button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={!enabled || loading}
            className="flex items-center gap-2 rounded-md bg-danger-500 px-4 py-2 font-bold text-[12px] uppercase tracking-[0.05em] text-white hover:bg-danger-600 disabled:opacity-50"
          >
            {loading && <Loader2 size={12} className="animate-spin" />}
            Apagar
          </button>
        </div>
      </div>
    </div>
  );
}

function formatPhone(waId: string): string {
  const c = waId.replace(/\D/g, '');
  if (c.startsWith('55') && (c.length === 12 || c.length === 13)) {
    const ddd = c.slice(2, 4);
    const rest = c.slice(4);
    if (rest.length === 9) return `+55 ${ddd} ${rest.slice(0, 5)}-${rest.slice(5)}`;
    if (rest.length === 8) return `+55 ${ddd} ${rest.slice(0, 4)}-${rest.slice(4)}`;
  }
  return `+${c}`;
}

// ───────────────────────── Ordenação (client-side) ─────────────────────────
// Mesmo padrão da Consulta: ordena os contatos JÁ CARREGADOS na tela. Como a
// lista usa scroll infinito, a ordenação cobre o que foi carregado (role até o
// fim pra ordenar o conjunto completo).
type ContactSortKey = 'name' | 'tags' | 'group' | 'first' | 'last';

function sortContacts(
  list: ContactRow[],
  key: ContactSortKey,
  dir: 'asc' | 'desc',
): ContactRow[] {
  const mul = dir === 'asc' ? 1 : -1;
  const ts = (v: string | null): number => (v ? new Date(v).getTime() : 0);
  return [...list].sort((a, b) => {
    let cmp = 0;
    switch (key) {
      case 'name':
        cmp = (a.display_name ?? '').localeCompare(b.display_name ?? '', 'pt-BR', {
          sensitivity: 'base',
        });
        break;
      case 'tags':
        // Ordena pelo nome da 1ª etiqueta (sem etiqueta agrupa junto no início
        // em ordem crescente / no fim em decrescente).
        cmp = (a.tags[0]?.name ?? '').localeCompare(b.tags[0]?.name ?? '', 'pt-BR', {
          sensitivity: 'base',
        });
        break;
      case 'group':
        // Ordena pelo nome do 1º grupo (sem grupo agrupa junto), igual etiquetas.
        cmp = (a.access_groups[0]?.name ?? '').localeCompare(
          b.access_groups[0]?.name ?? '',
          'pt-BR',
          { sensitivity: 'base' },
        );
        break;
      case 'first':
        cmp = ts(a.created_at) - ts(b.created_at);
        break;
      case 'last':
        cmp = ts(a.last_inbound_at) - ts(b.last_inbound_at);
        break;
    }
    return cmp * mul;
  });
}

function ContactSortTh({
  label,
  sortKey,
  currentKey,
  dir,
  onToggle,
}: {
  label: string;
  sortKey: ContactSortKey;
  currentKey: ContactSortKey | null;
  dir: 'asc' | 'desc';
  onToggle: (key: ContactSortKey) => void;
}) {
  const active = currentKey === sortKey;
  return (
    <th className="px-[14px] py-[10px] text-[11px] font-bold uppercase tracking-[.05em] text-n-500 whitespace-nowrap">
      <button
        type="button"
        onClick={() => onToggle(sortKey)}
        className={`flex items-center gap-1 uppercase ${active ? 'text-brand-600' : 'hover:text-n-700'}`}
      >
        {label}
        {active ? (
          dir === 'asc' ? (
            <ChevronUp size={12} />
          ) : (
            <ChevronDown size={12} />
          )
        ) : (
          <ChevronsUpDown size={12} className="text-n-400" />
        )}
      </button>
    </th>
  );
}
